/**
 * 10.6M — MISSÃO DIÁRIA.
 *
 * Uma missão por dia, igual para o dia inteiro e trocada à meia-noite (hora do aparelho).
 * O prêmio é bem maior que o das missões rotativas: é o motivo de voltar amanhã.
 *
 * Regras que valem a pena registrar:
 *   · NÃO é energia nem timer de espera. Você joga o quanto quiser; a diária é um objetivo
 *     extra por cima, nunca um portão que impede de jogar.
 *   · O sorteio é determinístico pelo DIA (mesmo índice o dia todo, em qualquer sessão):
 *     assim reabrir o jogo não troca a missão nem zera o progresso.
 *   · Se o jogador passar dias sem entrar, a diária simplesmente vira a do dia atual —
 *     nada de fila de atrasadas.
 *
 * Estado puro: quem conta os eventos e paga é o Game.
 */
import { GameConfig } from "../config/GameConfig";

export type DailyKind = "kill" | "elite" | "chest" | "territory" | "boss";

export interface DailyTemplate {
  id: string;
  kind: DailyKind;
  target: number;
  coins: number;
  xp: number;
  shards: number;
}

export interface DailySnapshot {
  /** dia (índice absoluto) a que o progresso pertence */
  day: number;
  progress: number;
  claimed: boolean;
  /** quantas diárias o jogador já concluiu (sequência total, para exibir) */
  total: number;
}

/** Índice do dia LOCAL (não UTC): a diária vira à meia-noite do aparelho. */
export function dayIndex(now = Date.now()): number {
  const d = new Date(now);
  return Math.floor((now - d.getTimezoneOffset() * 60000) / 86400000);
}

export class DailyMission {
  private day = dayIndex();
  private progress = 0;
  private claimed = false;
  private total = 0;

  constructor(private readonly playerLevel: () => number) {}

  /** Vira o dia se necessário. Chamar antes de ler qualquer coisa. */
  private roll(now = Date.now()): void {
    const d = dayIndex(now);
    if (d === this.day) return;
    this.day = d;
    this.progress = 0;
    this.claimed = false;
  }

  /** Template do dia — determinístico pelo índice do dia. */
  template(now = Date.now()): DailyTemplate {
    this.roll(now);
    const list = GameConfig.daily.templates as readonly DailyTemplate[];
    return list[((this.day % list.length) + list.length) % list.length];
  }

  /** Alvo e prêmio do dia, já escalados pelo nível do Caçador. */
  current(now = Date.now()): { t: DailyTemplate; target: number; coins: number; xp: number; shards: number; progress: number; claimed: boolean; ready: boolean } {
    const t = this.template(now);
    const c = GameConfig.daily;
    const lvl = Math.max(1, this.playerLevel());
    // teto no crescimento: em nível alto um alvo sem limite viraria uma diária impossível
    const grow = Math.min(c.targetMaxMult, 1 + (lvl - 1) * c.targetPerLevel);
    const target = Math.max(1, Math.round(t.target * grow));
    return {
      t,
      target,
      coins: Math.round(t.coins + lvl * c.coinsPerLevel),
      xp: Math.round(t.xp + lvl * c.xpPerLevel),
      shards: t.shards,
      progress: Math.min(target, this.progress),
      claimed: this.claimed,
      ready: !this.claimed && this.progress >= target,
    };
  }

  get timesDone(): number {
    return this.total;
  }

  /** Conta um evento. Devolve true se a diária ACABOU de ficar pronta. */
  advance(kind: DailyKind, amount = 1, now = Date.now()): boolean {
    const cur = this.current(now);
    if (cur.t.kind !== kind || this.claimed || this.progress >= cur.target) return false;
    this.progress = Math.min(cur.target, this.progress + amount);
    return this.progress >= cur.target;
  }

  /** Coleta o prêmio. Devolve o que pagar, ou null se não estava pronta. */
  claim(now = Date.now()): { coins: number; xp: number; shards: number } | null {
    const cur = this.current(now);
    if (!cur.ready) return null;
    this.claimed = true;
    this.total += 1;
    return { coins: cur.coins, xp: cur.xp, shards: cur.shards };
  }

  snapshot(): DailySnapshot {
    return { day: this.day, progress: this.progress, claimed: this.claimed, total: this.total };
  }

  load(data: DailySnapshot | undefined): void {
    const today = dayIndex();
    this.total = Math.max(0, Math.floor(Number(data?.total) || 0));
    if (!data || Math.floor(Number(data.day)) !== today) {
      // dia diferente (ou save antigo): começa a diária de hoje do zero
      this.day = today;
      this.progress = 0;
      this.claimed = false;
      return;
    }
    this.day = today;
    this.progress = Math.max(0, Math.floor(Number(data.progress) || 0));
    this.claimed = data.claimed === true;
  }

  reset(): void {
    this.day = dayIndex();
    this.progress = 0;
    this.claimed = false;
    this.total = 0;
  }
}
